import type { BookOrderStatus } from './types';

/**
 * Commit A0 — status → builder step, per v3 §7 (J-23), rewritten at
 * Position-5 Commit 6b.4 (Sebastian ruling 2026-09-02) for the post-cut
 * chain (summary_review before any image).
 *
 *   Leiste A — before payment, five marks (order changed at 6b.4):
 *     Personen · Geschichte · Figuren · Vorschau · Bestellung
 *   Leiste B — after payment, two marks:
 *     Bestellung · Freigabe
 *
 * Reason for the split (v3 §7): the print approval is a customer action
 * that arrives 20–80 minutes after payment, in a new session, via a
 * mail. As a sixth mark on a continuous bar it would sit for a step
 * the customer cannot reach in the same visit.
 *
 * ═══ 6b.4 RENAME + REORDER (Sebastian ruling 2026-09-02) ═════════════
 *
 * Pre-6b.4 the bar read `Personen · Titelbild · Geschichte · Vorschau ·
 * Bestellung` and `Titelbild` sat at position 2. Two problems that the
 * rewrite closes at once:
 *
 *   (a) After the summary-review cut, the customer sees the SUMMARY
 *       (Geschichte) BEFORE any image. So "Titelbild" cannot sit
 *       before "Geschichte" any more.
 *   (b) "Titelbild" was already inaccurate. What the customer approves
 *       at the anchor gate is the anchor image — the figures as they
 *       will look throughout the book. The cover derives from it but
 *       is not the thing itself. The new label "Figuren" pairs with
 *       "Personen": there the customer enters who is in the book,
 *       here he sees how they look.
 *
 * The new order is monotonic: every customer action bumps the step
 * value strictly forward (1 → 2 → 3 → 4 → 5). No backwards walk.
 *
 * ═══ WHY BAR B DISTINGUISHES WAITING FROM APPROVAL ═══════════════════
 *
 * `paid` / `rendering_full` / `qa_*` and `customer_review` render
 * DIFFERENT visual states on Bar B, not the same one:
 *
 *   - during production (`paid`, `rendering_full`, `qa_running`,
 *     `qa_regen`, `qa_human_queue`) the customer waits and cannot act.
 *     Bar B: Bestellung DONE, Freigabe OPEN. Rendering "Freigabe active"
 *     here would tell the customer he can act when he cannot.
 *   - once the book is preview-ready for the customer
 *     (`customer_review`) the print approval IS the current action.
 *     Bar B: Bestellung DONE, Freigabe ACTIVE.
 *
 * ═══ WHY `anchor_approved` LIVES ON STEP 3 (Figuren) ═════════════════
 *
 * Pre-6b.4 the v3 §7 comment said "`anchor_approved` gehört auf
 * Geschichte, nicht ins Leere: nach J-05 ist der Accept am Anker-Gate
 * das Ereignis, das die Geschichte anstößt." That reasoning was
 * correct when the anchor sat at step 2 (Titelbild) and Geschichte
 * was the NEXT mark — leaving anchor_approved on Geschichte kept the
 * bar moving forward.
 *
 * At 6b.4 the anchor lives at step 3 (Figuren) and story_generating
 * sits at step 4 (Vorschau — the wait for the story-then-preview).
 * `anchor_approved` is a transient state right after the customer
 * accepts the anchor: he has just finished Figuren and the story
 * generation queue has not yet picked up the job. The bar mark that
 * matches the step he just finished is Figuren. Grouping with
 * `anchor_pending` / `anchor_review` keeps a reload landing on
 * `anchor_approved` visually on the step the customer completed one
 * click earlier, which is what he expects.
 *
 * ═══ S-19 IS A COMPONENT HOOK, NOT A MAPPER WIDENING ═════════════════
 *
 * v3 §9 S-19 records that no status rests on Bar A's mark 5
 * ("Bestellung") today. The future screen 5 (Bestellung, Phase 6)
 * therefore needs a way to render Bar A with Bestellung active without
 * a status to derive it from.
 *
 * That override lives on the `Schrittleiste` component as an optional
 * `activeMarkOverride` prop, NOT on this mapper's return union. Reason:
 * a return type the function can never produce forces every future
 * consumer to handle a case that cannot occur. The mapper stays
 * truthful about the statuses that actually produce marks; the
 * exception lives at the screen that has no status.
 *
 * ═══ MAPPING TABLE (6b.4) ════════════════════════════════════════════
 *
 * | Status                                                          | Leiste | Aktive Marke                          |
 * |---|---|---|
 * | draft_configuring, light_gate_pending                           | A      | Personen                              |
 * | summary_review                                                  | A      | Geschichte                            |
 * | anchor_pending, anchor_review, anchor_approved                  | A      | Figuren                               |
 * | story_generating, text_editing, preview_rendering, preview_ready| A      | Vorschau                              |
 * | checkout_pending                                                | A      | Bestellung (override)                 |
 * | paid, rendering_full, qa_running, qa_regen, qa_human_queue      | B      | Bestellung erledigt, Freigabe OFFEN   |
 * | customer_review                                                 | B      | Bestellung erledigt, Freigabe AKTIV   |
 * | photo_rejected                                                  | A      | Personen (special variant)            |
 * | print_approved, prepress, handoff_pod, shipped, delivered,      | —      | —                                     |
 * | paused_billing, reclamation_open, cancelled                     |        |                                       |
 *
 * The eight statuses in the last row currently render NO bar. Phase 7
 * owns their bar projection; the cross-states (`paused_billing`,
 * `reclamation_open`, `cancelled`) wait on a separate Sebastian ruling.
 * Tracker `[book-schrittleiste-unknown-post-print]` names this.
 */
export type BookBuilderStep =
  | { readonly kind: 'step'; readonly value: 1 | 2 | 3 | 4 | 5 }
  | { readonly kind: 'post_payment_waiting' }
  | { readonly kind: 'post_payment_approval' }
  /**
   * `photo_rejected` renders Bar A with Personen active — the
   * customer is being sent back to the photo he picked in Schritt 1
   * (Sebastian ruling 2026-08-06 coordination brief). Its own
   * variant, NOT a fold into `post_payment_waiting` or a step-1
   * alias: the customer is pre-payment and the screen renders
   * different content from Schritt-1's fresh-configurator flow. The
   * Schrittleiste maps this to Bar A / Personen active internally.
   */
  | { readonly kind: 'photo_rejected' }
  | { readonly kind: 'unknown' };

/**
 * Pure function. Reads only its argument, returns a fresh object.
 * No branch touches state, no branch touches I/O.
 */
export function mapStatusToBuilderStep(status: BookOrderStatus): BookBuilderStep {
  switch (status) {
    case 'draft_configuring':
    case 'light_gate_pending':
      return { kind: 'step', value: 1 };
    // Position-5 Commit 6b.4 (Sebastian ruling 2026-09-02) —
    // summary_review is Bar A step 2 (Geschichte). The pre-model
    // summary IS the "Geschichte" step under Position-5; the customer
    // reads + releases here before any image exists.
    case 'summary_review':
      return { kind: 'step', value: 2 };
    // 6b.4 — anchor states cluster on Bar A step 3 (Figuren).
    // anchor_approved joins the anchor group (rather than the story
    // group as before 6b.4) because the anchor now sits at step 3
    // and Figuren is the step the customer just finished — see the
    // "WHY anchor_approved LIVES ON STEP 3 (Figuren)" section in the
    // file header for the full rationale.
    case 'anchor_pending':
    case 'anchor_review':
    case 'anchor_approved':
      return { kind: 'step', value: 3 };
    // 6b.4 — story_generating + text_editing + preview_* on step 4
    // (Vorschau). All four are "the story exists / is arriving /
    // will show as a preview" — one mark, monotonic 3 → 4 after the
    // anchor is accepted.
    case 'story_generating':
    case 'text_editing':
    case 'preview_rendering':
    case 'preview_ready':
      return { kind: 'step', value: 4 };
    case 'checkout_pending':
      // 6-3 Bildschirm 5 (Sebastian ruling 2026-08-15). Bar A stays
      // active with mark 5 = "Bestellung". Closes tracker
      // [pwa-status-to-step-add-checkout-pending] filed with 6-1.
      // Re-entry from Stripe abandonment lands here; BestellungRoute
      // renders BestellungScreen; the customer can either resume
      // (BookCheckoutService.tryResumeOpenSession returns the same
      // Stripe URL) or roll back to preview_ready (on session
      // expiry).
      return { kind: 'step', value: 5 };
    case 'paid':
    case 'rendering_full':
    case 'qa_running':
    case 'qa_regen':
    case 'qa_human_queue':
      return { kind: 'post_payment_waiting' };
    case 'customer_review':
      return { kind: 'post_payment_approval' };
    case 'photo_rejected':
      return { kind: 'photo_rejected' };
    case 'print_approved':
    case 'prepress':
    case 'handoff_pod':
    case 'shipped':
    case 'delivered':
    case 'paused_billing':
    case 'reclamation_open':
    case 'cancelled':
      return { kind: 'unknown' };
    // Sebastian ruling 2026-09-08 evening (Commit 2 of A+B+C) —
    // release-chain terminal-fail state. Maps to `unknown` (no bar)
    // like paused_billing / reclamation_open / cancelled: the state
    // is a not-progressing stop point, and the released error copy
    // says "es geht nichts verloren" — showing forward-progress on
    // a bar would contradict the message. When the operator recovers
    // (flip back to summary_review or anchor_pending), the customer's
    // next poll picks up the new status and the mapper returns the
    // appropriate bar for that recovery state.
    case 'book_release_chain_error':
      return { kind: 'unknown' };
    // Sebastian ruling 2026-09-10 (Paket 3) — image-refusal state.
    // Maps to `unknown` (no bar): same rationale as
    // book_release_chain_error — a not-progressing stop point, the
    // customer must change something rather than keep advancing. If
    // Sebastian later rules that image_refused should render Bar A /
    // Figuren active (mirror of photo_rejected's Bar A / Personen
    // treatment), widen this to its own kind + Schrittleiste branch.
    case 'image_refused':
      return { kind: 'unknown' };
    // 2026-10-14 — per-figure block-derivation rejection state. Maps
    // to step 1 (Personen) because the customer must fix one or more
    // figures (swap photo / remove figure); PersonenRoute reads the
    // envelope's `intake.derived.rejections` map and renders a hint
    // on the affected figure(s). Mirrors how `photo_rejected` sits
    // on Bar A / Personen (same pre-payment customer-fix pattern).
    case 'block_derivation_rejected':
      return { kind: 'step', value: 1 };
  }
}
